import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import {
  getAllSecurityIncidents,
  linkVideoClipsToIncident,
} from "../../../service/securityService";
import type { SecurityIncident } from "../../../types/security_types";

const IncidentVideoClipsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [incidentId, setIncidentId] = useState("");
  const [clipUrl, setClipUrl] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadIncidents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllSecurityIncidents(undefined, { limit: 100, page: 1 });
      setIncidents(res.data);
      setIncidentId((prev) => {
        if (prev) return prev;
        const first = res.data[0];
        return first ? first.id || first._id || "" : "";
      });
    } catch (e) {
      console.error(e);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!incidentId) {
      toast.error("Select an incident");
      return;
    }
    if (!clipUrl.trim()) {
      toast.error("Clip URL is required");
      return;
    }
    setSubmitting(true);
    try {
      await linkVideoClipsToIncident(incidentId, {
        url: clipUrl.trim(),
        cameraId: cameraId.trim() || undefined,
        timestamp: new Date().toISOString(),
      });
      toast.success("Video clip linked to incident");
      setClipUrl("");
      setCameraId("");
    } catch (err) {
      console.error(err);
      toast.error("Could not link clip");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-2 py-4 mt-14 min-h-screen space-y-6 max-w-xl">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/security/surveillance")}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Overview
        </button>
        <div className="bg-[#EAFBFA] p-3 rounded-lg">
          <Tag className="w-6 h-6 text-[#0fa9a6]" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Incident tagging from video</h2>
          <p className="text-sm text-gray-600">
            Attach clip URLs and optional camera metadata to a security incident record.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <SpinLoader />
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4"
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Incident</label>
            <select
              value={incidentId}
              onChange={(e) => setIncidentId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              required
            >
              <option value="">Select incident</option>
              {incidents.map((inc) => {
                const id = inc.id || inc._id || "";
                const label =
                  inc.incidentNumber ||
                  inc.description?.slice(0, 48) ||
                  id ||
                  "Incident";
                return (
                  <option key={id} value={id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Clip URL</label>
            <input
              type="url"
              value={clipUrl}
              onChange={(e) => setClipUrl(e.target.value)}
              placeholder="https://…"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Camera ID (optional)</label>
            <input
              type="text"
              value={cameraId}
              onChange={(e) => setCameraId(e.target.value)}
              placeholder="e.g. ICU_CAM_01"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || incidents.length === 0}
            className="w-full py-2.5 rounded-full bg-[#0fa9a6] text-white font-semibold text-sm hover:bg-teal-600 disabled:opacity-50"
          >
            {submitting ? "Linking…" : "Link clip to incident"}
          </button>
        </form>
      )}
    </div>
  );
};

export default IncidentVideoClipsScreen;
